import * as React from "react";
import { AuditTrailEvent, AuditTrailEntity, QueryOrderDirection, Query } from "../../misc/api/apiTypes";
import * as _ from 'lodash';
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { DateTimeAccuracy, LocalizedDateTime } from "../../misc/components/LocalizedDateTime";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { AuditTrailActionEnum } from "../../misc/api/ApiAuditActions";
import { useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";

interface HistoryReviewSummaryProps {
    event: AuditTrailEvent;
}

const GetReviewEntity = (eventDetails: AuditTrailEvent) => eventDetails.Entities.find(e => e.TypeName === "CoughAnalysisSetReview");

const TypeFilter = { Member: "TypeName", Values: ["CoughAnalysisSetReview"] };
const initialQueryState: Query<AuditTrailEntity> = {
    Data: null,
    Filters: [TypeFilter],
    Order: { Member: "Event.EventDateTime", Direction: QueryOrderDirection.Descending }
};

const CommentsForReviewAction = ({ eventId }): JSX.Element => {

    const [loading, setLoading] = React.useState<boolean>();
    const [failedToLoad, setFailedToLoad] = React.useState<boolean>(false);
    const [eventDetails, setEventDetails] = React.useState<AuditTrailEvent>();
    const legacyClient = useWebPortalLegacyClient();

    const fetchEventDetails = async () => {
        try {
            setLoading(true);
            const eventDetails = await legacyClient.getAuditTrailEvent(eventId);
            setEventDetails(eventDetails);
            setFailedToLoad(false);
        } catch {
            setFailedToLoad(true);
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {

        fetchEventDetails();

    }, []);

    const renderChangeForProperty = (propertyName: string) => {
        const entityChange = GetReviewEntity(eventDetails);
        const changedComment = entityChange.Details.find(d => d.PropertyName === propertyName);

        return (changedComment ? <li key={propertyName}>{propertyName}: <del>{changedComment.OriginalValue}</del> {"->"} <ins>{changedComment.NewValue}</ins></li> : <li key={propertyName}>{propertyName}: No Change</li>)
    };

    const renderEventDetails = () => {

        return (!failedToLoad ? <ul>
            {["Process", "Reason", "Details", "FreeText"].map(renderChangeForProperty)}
        </ul> : <Alert show={true} category={AlertCategoryEnum.Danger}>
                An error occurred when loading the details.
                </Alert>);
    };

    return (!eventDetails || loading ? <LoadingWithMessage message="Loading Changed Comments..." /> : renderEventDetails());
}

const renderAction = (action: AuditTrailEvent) => {

    return (
        <tr key={action.Id}>
            <td>
                <LocalizedDateTime
                    time={action.EventDateTime}
                    accuracy={DateTimeAccuracy.Second}
                />
            </td>
            <td>{action.ActionName}</td>
            <td><CommentsForReviewAction eventId={action.Id} /></td>
        </tr>
    );
}

const HistoryReviewSummary = ({ event }: HistoryReviewSummaryProps): JSX.Element => {

    const validActions = [AuditTrailActionEnum.ReviewRequired, AuditTrailActionEnum.UpdateCoughAnalysisSetReviewComments, AuditTrailActionEnum.ResetCoughAnalysisSetReview, AuditTrailActionEnum.ReviewComplete];

    const [loading, setLoading] = React.useState<boolean>();
    const [reviewActions, setReviewActions] = React.useState<Query<AuditTrailEntity>>(initialQueryState);
    const [failedToLoad, setFailedToLoad] = React.useState<boolean>(false);
    const [eventDetails, setEventDetails] = React.useState<AuditTrailEvent>();
    const legacyClient = useWebPortalLegacyClient();

    const fetchReviewActions = async (recordId: number) => {
        try {
            setLoading(true);
            const actionsForThisReview = await legacyClient.getAnalysisSetAuditEntities({
                ...initialQueryState,
                Data: null,
                Filters: initialQueryState.Filters.concat([{ Member: "RecordId", Values: [recordId] }])
            });
            setReviewActions(actionsForThisReview);
            setFailedToLoad(false);
        } catch {
            setFailedToLoad(true);
        } finally {
            setLoading(false);
        }
    }

    const fetchEventDetails = async () => {
        try {
            setLoading(true);
            const eventDetails = await legacyClient.getAuditTrailEvent(event.Id);
            setEventDetails(eventDetails);
            setFailedToLoad(false);
        } catch {
            setFailedToLoad(true);
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {

        if (!eventDetails) {
            return;
        }

        async function GetReviewActions(recordId: number) {
            await fetchReviewActions(recordId);
        }

        const reviewEntity = GetReviewEntity(eventDetails)

        if (reviewEntity) {
            GetReviewActions(reviewEntity.RecordId);
        }

    }, [eventDetails]);

    const renderButton = () => {
        return (<button className="btn btn-primary" onClick={fetchEventDetails}>Show Review Summary</button>);
    }

    const renderEventDetails = () => {

        return (
            !failedToLoad ? <table className="table table-striped table-bordered">
                <thead>
                    <tr>
                        <th>
                            Date
                                </th>
                        <th>
                            Action
                                </th>
                        <th>
                            Changed Comments
                                </th>
                    </tr>
                </thead>
                <tbody>
                    {_.uniqBy(reviewActions.Data, data => data.Event.Id).map(e => renderAction(e.Event))}
                </tbody>
            </table> : <Alert show={true} category={AlertCategoryEnum.Danger}>
                    An error occurred when loading the details.
                </Alert>);
    }

    const renderDetails = () => {
        return (!reviewActions || !reviewActions.Data || loading ? <LoadingWithMessage message="Loading Review Summary..." /> : renderEventDetails());
    }

    const renderBody = () => {

        return (<div className="row">
            <div className="col-md-12">
                {!!eventDetails || loading ? renderDetails() : renderButton()}
            </div>
        </div>);
    }


    return (validActions.find(a => a === event.ActionId) ? renderBody() : null);
}

export default HistoryReviewSummary;
